import { Box, forwardRef } from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import {
  EndCapShape,
  ProgressColorScheme,
} from '@allergan-data-labs/alle-elements-progress-bar';
import {
  getBarStyles,
  getContainerStyles,
  getRotationStyle,
} from './progress-circle.theme';

export interface ProgressCircleProps {
  /** If true, the progress circle will animate for every change in value
   * @default true
   */
  animate?: boolean;

  /** The color mode that the progress circle will display.
   * @default light
   */
  colorMode?: ColorMode;

  /** The visual color appearance of the progress circle
   * @default 'brand'
   */
  colorScheme?: ProgressColorScheme;

  /** The the shape of the beginning and end of the progress circle.
   * @default 'round'
   */
  endCapShape?: EndCapShape;

  /** If true, the progress circle will animate continuously.
   * @default false
   */
  isIndeterminate?: boolean;

  /** The size of the progress circle
   * @default md
   */
  size?: 'sm' | 'md' | 'lg';

  /** The value of the progress circle. If undefined the progress circle will be in indeterminate state */
  value?: number;
}

/**
 * Progress Circle is used to indicates the progress for determinate and indeterminate processes.
 */

export const ProgressCircle = forwardRef<ProgressCircleProps, 'div'>(
  (
    {
      animate,
      colorMode: propColorMode,
      colorScheme,
      endCapShape,
      isIndeterminate,
      size,
      value,
      ...rest
    }: ProgressCircleProps,
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const circleWidth = size === 'sm' ? 16 : size === 'lg' ? 24 : 20;
    const lineThickness = circleWidth / 10;
    const radius = circleWidth / 2 - lineThickness;
    return (
      <Box
        ref={ref}
        role="progressbar"
        aria-label={`progress-circle-${colorScheme || 'brand'}`}
        aria-busy={isIndeterminate || value === undefined}
        aria-valuenow={isIndeterminate || value === undefined ? 0 : value}
        aria-valuemin={0}
        aria-valuemax={100}
        data-testid={'progress-circle'}
        width={circleWidth}
        height={circleWidth}
        sx={getRotationStyle({ isIndeterminate, value })}
        {...rest}
      >
        <svg
          data-testid={'progress-circle-container'}
          width={circleWidth}
          height={circleWidth}
          viewBox={`0 0 ${circleWidth} ${circleWidth}`}
        >
          <circle
            data-testid={'progress-circle-background'}
            cx={circleWidth / 2}
            cy={circleWidth / 2}
            r={radius}
            style={getContainerStyles({
              lineThickness,
              colorMode,
              colorScheme,
            })}
          />
          <circle
            data-testid={'progress-circle-foreground'}
            cx={circleWidth / 2}
            cy={circleWidth / 2}
            r={radius}
            transform={`rotate(-90 ${circleWidth / 2} ${circleWidth / 2})`}
            style={getBarStyles({
              animate,
              circleWidth,
              colorMode,
              colorScheme,
              endCapShape,
              isIndeterminate,
              lineThickness,
              value,
            })}
          />
        </svg>
      </Box>
    );
  }
);

export default ProgressCircle;
